// StumbleMap — stylized in-vibe treasure map (per ADR-0018).
// Ported from design-canon/project/src/components.jsx → StumbleMap
// Variants: "treasure" (MVP default) | "blobs" | "grid-style"
// No tile-server dependency — SVG + CSS only.

import type { CSSProperties } from 'react'
import { Pin, type PinSpec } from './Pin'

export type MapVariant = 'treasure' | 'blobs' | 'grid-style'

export type TrailPoint = { x: number; y: number }

export type StumbleMapProps = {
  variant?: MapVariant
  height?: number
  pins?: PinSpec[]
  trail?: TrailPoint[]
  you?: { x: number; y: number } | null
  label?: string
  compact?: boolean
  style?: CSSProperties
}

export function StumbleMap({
  variant = 'treasure',
  height = 280,
  pins = [],
  trail = [],
  you,
  label,
  compact = false,
  style,
}: StumbleMapProps) {
  const w = 100
  const h = 100
  return (
    <div className={`map ${variant}`} style={{ height, ...style }}>
      {variant === 'treasure' && (
        <>
          <div className="streets" />
          <div className="block" style={{ left: '8%', top: '12%', width: '22%', height: '22%' }} />
          <div className="block" style={{ left: '44%', top: '8%', width: '18%', height: '16%' }} />
          <div className="block" style={{ left: '72%', top: '18%', width: '20%', height: '20%' }} />
          <div className="block" style={{ left: '12%', top: '52%', width: '26%', height: '18%' }} />
          <div className="block" style={{ left: '52%', top: '60%', width: '22%', height: '22%' }} />
          <div
            className="park"
            style={{ left: '44%', top: '38%', width: '18%', height: '18%', borderRadius: 999 }}
          />
          <div className="water" style={{ left: '-8%', top: '78%', width: '62%', height: '22%' }} />
        </>
      )}

      {variant === 'blobs' && (
        <>
          <div
            className="blob"
            style={{
              left: '4%',
              top: '6%',
              width: '44%',
              height: '38%',
              background: 'var(--pink)',
              borderRadius: '60% 40% 50% 50%',
            }}
          />
          <div
            className="blob"
            style={{
              left: '52%',
              top: '10%',
              width: '44%',
              height: '32%',
              background: 'var(--orange)',
              borderRadius: '50% 60% 40% 50%',
            }}
          />
          <div
            className="blob"
            style={{
              left: '8%',
              top: '48%',
              width: '36%',
              height: '44%',
              background: 'var(--teal)',
              borderRadius: '55% 45% 60% 40%',
            }}
          />
          <div
            className="blob"
            style={{
              left: '48%',
              top: '52%',
              width: '48%',
              height: '44%',
              background: 'var(--lime)',
              borderRadius: '40% 60% 50% 50%',
            }}
          />
        </>
      )}

      {/* trail polyline */}
      <svg
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
        viewBox={`0 0 ${w} ${h}`}
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <title>Stumble trail</title>
        {trail.length > 1 && (
          <polyline
            points={trail.map((p) => `${p.x},${p.y}`).join(' ')}
            fill="none"
            stroke="var(--line)"
            strokeWidth="1.2"
            strokeDasharray="2 1.5"
            strokeLinecap="round"
          />
        )}
      </svg>

      {/* pins — data-testid + data-x/data-y on Pin itself so Playwright
          toBeVisible() works (the previous display:contents wrapper had no
          layout box, breaking visibility checks). */}
      {pins.map((p, i) => (
        <Pin
          key={`${p.x}-${p.y}-${i}`}
          testid={`map-pin-${i}`}
          dataX={p.x}
          dataY={p.y}
          color={p.color || 'var(--orange)'}
          label={p.label}
          size={p.size}
          style={{ left: `${p.x}%`, top: `${p.y}%` }}
        />
      ))}

      {/* you-are-here dot */}
      {you && (
        <div
          style={{
            position: 'absolute',
            left: `${you.x}%`,
            top: `${you.y}%`,
            transform: 'translate(-50%, -50%)',
            width: 22,
            height: 22,
            borderRadius: 999,
            background: 'var(--paper)',
            border: '3px solid var(--line)',
            boxShadow: '0 0 0 6px rgba(233,90,169,0.35)',
          }}
          aria-label="You are here"
        />
      )}

      {label && !compact && (
        <div
          style={{
            position: 'absolute',
            left: 12,
            bottom: 12,
            padding: '8px 12px',
            border: '2px solid var(--line)',
            borderRadius: 999,
            background: 'var(--paper)',
            fontFamily: 'var(--font-stamp)',
            fontSize: 12,
            letterSpacing: '0.08em',
          }}
        >
          {label}
        </div>
      )}
    </div>
  )
}
